import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../../utils/api';
import './CteDiarioReport.css';

interface Venda {
  id?: number;
  data_venda?: string;
  valor_total?: number;
  cliente?: string;
  produto?: string;
  quantidade?: number;
}

interface FiltroPeriodo {
  dataInicio: string;
  dataFim: string;
}

interface DiaFaturamento {
  data: string;
  dataLabel: string;
  totalFaturamento: number;
  quantidadeCtEs: number;
  ticketMedio: number;
  clientes: number;
}

interface DataRange {
  dataMin?: string | null;
  dataMax?: string | null;
}

const toDateParam = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getPeriodoInicial = (): FiltroPeriodo => {
  const hoje = new Date();
  const inicio = new Date(hoje.getTime() - 1000 * 60 * 60 * 24 * 30);
  return {
    dataInicio: toDateParam(inicio),
    dataFim: toDateParam(hoje),
  };
};

const parseValor = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR');
};

const formatDateShort = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}`;
};

const CteDiarioReport: React.FC = () => {
  const [periodo, setPeriodo] = useState<FiltroPeriodo>(() => getPeriodoInicial());
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [filtroCliente, setFiltroCliente] = useState('Todos');
  const [dataRange, setDataRange] = useState<DataRange>({});

  const carregar = async () => {
    setLoading(true);
    setErro('');

    try {
      const response = await api.get('/vendas', {
        params: {
          dataInicio: periodo.dataInicio,
          dataFim: periodo.dataFim,
        },
      });

      setDataRange(response.data?.dataRange || {});
      setVendas(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Não foi possível carregar o faturamento no momento.';
      setErro(message);
      setDataRange({});
      setVendas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientes = useMemo(() => {
    const unique = new Set(vendas.map(item => String(item.cliente || '').trim()).filter(Boolean));
    return ['Todos', ...Array.from(unique).sort((a, b) => a.localeCompare(b))];
  }, [vendas]);

  const vendasFiltradas = useMemo(() => {
    if (filtroCliente === 'Todos') return vendas;
    return vendas.filter(item => String(item.cliente || '').trim() === filtroCliente);
  }, [vendas, filtroCliente]);

  const faturamentoPorDia = useMemo(() => {
    const mapa = new Map<string, { faturamento: number; ctes: number; clientes: Set<string> }>();

    vendasFiltradas.forEach(item => {
      const data = item.data_venda ? item.data_venda.split('T')[0] : 'Sem data';
      const atual = mapa.get(data) || { faturamento: 0, ctes: 0, clientes: new Set() };
      atual.faturamento += parseValor(item.valor_total);
      atual.ctes += 1;
      if (item.cliente) atual.clientes.add(String(item.cliente).trim());
      mapa.set(data, atual);
    });

    const diasOrdenados: DiaFaturamento[] = Array.from(mapa.entries())
      .map(([data, values]) => {
        const totalVendas = values.ctes;
        const totalFaturamento = values.faturamento;
        return {
          data,
          dataLabel: formatDateShort(data),
          totalFaturamento,
          quantidadeCtEs: totalVendas,
          ticketMedio: totalVendas ? totalFaturamento / totalVendas : 0,
          clientes: values.clientes.size,
        };
      })
      .sort((a, b) => a.data.localeCompare(b.data));

    return diasOrdenados;
  }, [vendasFiltradas]);

  const resumo = useMemo(() => {
    const totalFaturamento = vendasFiltradas.reduce((acc, item) => acc + parseValor(item.valor_total), 0);
    const totalCtEs = vendasFiltradas.length;
    const ticketMedio = totalCtEs ? totalFaturamento / totalCtEs : 0;
    const diasComVenda = new Set(faturamentoPorDia.map(d => d.data)).size;

    return {
      totalFaturamento,
      totalCtEs,
      ticketMedio,
      diasComVenda,
    };
  }, [vendasFiltradas, faturamentoPorDia]);

  const maiorFaturamentoDia = useMemo(() => {
    return faturamentoPorDia.reduce((max, item) => Math.max(max, item.totalFaturamento), 0);
  }, [faturamentoPorDia]);

  const faixaDisponivel = useMemo(() => {
    const min = dataRange?.dataMin ? String(dataRange.dataMin).slice(0, 10) : '';
    const max = dataRange?.dataMax ? String(dataRange.dataMax).slice(0, 10) : '';
    if (!min || !max) return '';
    return `${formatDate(min)} a ${formatDate(max)}`;
  }, [dataRange]);

  const aplicarFaixaDisponivel = () => {
    const min = dataRange?.dataMin ? String(dataRange.dataMin).slice(0, 10) : '';
    const max = dataRange?.dataMax ? String(dataRange.dataMax).slice(0, 10) : '';
    if (!min || !max) return;

    setPeriodo({ dataInicio: min, dataFim: max });
    setFiltroCliente('Todos');
    setTimeout(() => {
      carregar();
    }, 0);
  };

  const exportCsv = () => {
    const headers = ['Data', 'Faturamento', 'CTEs', 'Ticket Médio', 'Clientes Únicos'];
    const escape = (value: unknown) => {
      const text = String(value ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ');
      return `"${text}"`;
    };

    const content = [
      '\ufeff',
      headers.map(escape).join(';'),
      ...faturamentoPorDia.map((item) => [
        item.data,
        item.totalFaturamento.toFixed(2),
        item.quantidadeCtEs,
        item.ticketMedio.toFixed(2),
        item.clientes,
      ].map(escape).join(';')),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `faturamento_cte_diario_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cte-report cte-pbi-canvas">
      <div className="cte-pbi-header">
        <div>
          <h3>Faturamento CTE FKS Diário</h3>
          <p>Acompanhamento diário detalhado do faturamento de CTEs, com análise de performance por dia.</p>
        </div>
      </div>

      <div className="cte-toolbar cte-visual-card">
        <div className="cte-filtros">
          <label>
            Início
            <input
              type="date"
              value={periodo.dataInicio}
              onChange={(e) => setPeriodo(prev => ({ ...prev, dataInicio: e.target.value }))}
            />
          </label>
          <label>
            Fim
            <input
              type="date"
              value={periodo.dataFim}
              onChange={(e) => setPeriodo(prev => ({ ...prev, dataFim: e.target.value }))}
            />
          </label>
          <label>
            Cliente
            <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
              {clientes.map(cliente => (
                <option key={cliente} value={cliente}>{cliente}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="cte-actions">
          <button className="cte-btn" onClick={carregar} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button className="cte-btn cte-btn-ghost" onClick={exportCsv} disabled={!faturamentoPorDia.length}>
            Exportar CSV
          </button>
        </div>
      </div>

      {erro && <div className="cte-error">{erro}</div>}
      {!erro && vendas.length === 0 && faixaDisponivel && (
        <div className="cte-info">
          <span>Sem dados no período selecionado. Dados disponíveis de {faixaDisponivel}.</span>
          <button className="cte-btn cte-btn-ghost" onClick={aplicarFaixaDisponivel}>
            Usar período com dados
          </button>
        </div>
      )}

      <div className="cte-kpis">
        <div className="cte-kpi-card cte-visual-card">
          <span>Faturamento Total</span>
          <strong>{formatCurrency(resumo.totalFaturamento)}</strong>
        </div>
        <div className="cte-kpi-card cte-visual-card">
          <span>Total CTEs</span>
          <strong>{formatNumber(resumo.totalCtEs)}</strong>
        </div>
        <div className="cte-kpi-card cte-visual-card">
          <span>Ticket Médio</span>
          <strong>{formatCurrency(resumo.ticketMedio)}</strong>
        </div>
        <div className="cte-kpi-card cte-visual-card">
          <span>Dias com Venda</span>
          <strong>{formatNumber(resumo.diasComVenda)}</strong>
        </div>
      </div>

      <div className="cte-chart cte-visual-card">
        <h4>Faturamento por Dia</h4>
        {faturamentoPorDia.length === 0 ? (
          <p className="cte-empty">
            Sem dados para o período selecionado.
            {faixaDisponivel ? ` Faixa disponível: ${faixaDisponivel}.` : ''}
          </p>
        ) : (
          <div className="cte-bars">
            {faturamentoPorDia.map(item => {
              const width = maiorFaturamentoDia > 0 ? Math.max((item.totalFaturamento / maiorFaturamentoDia) * 100, 4) : 0;
              return (
                <div className="cte-bar-row" key={item.data}>
                  <div className="cte-bar-label">{item.dataLabel}</div>
                  <div className="cte-bar-track">
                    <div className="cte-bar-fill" style={{ width: `${width}%` }} />
                  </div>
                  <div className="cte-bar-value">{formatCurrency(item.totalFaturamento)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="cte-table-wrap cte-visual-card">
        <h4>Detalhamento por Dia</h4>
        <div className="cte-table-scroll">
          <table className="cte-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Faturamento</th>
                <th>CTEs</th>
                <th>Ticket Médio</th>
                <th>Clientes Únicos</th>
              </tr>
            </thead>
            <tbody>
              {faturamentoPorDia.length === 0 ? (
                <tr>
                  <td colSpan={5} className="cte-empty-cell">Nenhuma venda encontrada.</td>
                </tr>
              ) : (
                faturamentoPorDia.map((item) => (
                  <tr key={item.data}>
                    <td>{formatDate(item.data)}</td>
                    <td>{formatCurrency(item.totalFaturamento)}</td>
                    <td>{formatNumber(item.quantidadeCtEs)}</td>
                    <td>{formatCurrency(item.ticketMedio)}</td>
                    <td>{formatNumber(item.clientes)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CteDiarioReport;
